import { useCallback, useEffect, useState } from 'react';
import { bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';
import { CLINIC_APP_URL } from '../lib/runtimeEnv';

// «رفتن به پنل کلینیک» — may this account enter the clinic panel, and as which
// clinic?
//
// Both questions in ONE document, because neither existing query answers it
// alone:
//   • `myOwnedClinics` = clinicmanagement rows whose keycloak_user_id is this
//     sub, i.e. clinics this account registered. Owning one is what earns the
//     `clinic_owner` role + `clinic_id` attribute (bff.web lib/kcAdmin.js), so
//     an owned clinic is always enterable.
//   • `myClinics` = doctorpreference `clinic_doctors` memberships, which is how
//     a doctor who joined someone else's clinic by code appears — and that one
//     carries a `status`, because a membership can still be pending.
// No new BFF field is added: the clinic BFF authorises on the token's roles
// anyway (clinic_owner / clinic_admin / clinic_staff), so this is chrome
// deciding what to show, not an access decision.
//
// `currency` rides along because these are also "the doctor's clinics" for the
// money screens: the accounting filter lists them, and an EMPTY report or day —
// which has no row to read a currency from — states its zero in the clinic's.
const CLINIC_SWITCH = `
  query ClinicSwitchTargets {
    myOwnedClinics { id name city currency }
    myClinics {
      id
      status
      clinic { id name city currency }
    }
  }
`;

// The membership states that let the doctor in. The practitioner service
// writes exactly two words into `clinic_doctors.status`: a link is born
// `pending` and `AcceptClinicDoctor` writes `accepted` — the same pair
// SettingsKit's membership badge knows, and the one the clinic BFF's
// `practitionerStatus` maps to ACTIVE.
//
// This set used to hold only `active`/`approved`, words nothing writes, so every
// clinic a doctor had JOINED was dropped and only the one they owned survived:
// پذیرش then locked its lens to that clinic, hid the others' appointments, and
// its «نمایش همه» had nothing to widen to. `active`/`approved` stay for older
// rows. Anything else (pending, invited, suspended, a value this client has
// never seen) is NOT an entry ticket: the clinic BFF would answer FORBIDDEN and
// the doctor would land on a dead panel.
const ENTERABLE = new Set(['accepted', 'active', 'approved']);

// `currency` is passed through as the BFF sent it; '' reads IRR wherever it is
// used (lib/clinicMoney), which is also what an older BFF's missing field means.
function toTargets(data) {
  const byId = new Map();
  for (const c of data?.myOwnedClinics || []) {
    if (c?.id) byId.set(c.id, { id: c.id, name: c.name || '', city: c.city || '', currency: c.currency || '', owned: true });
  }
  for (const link of data?.myClinics || []) {
    const c = link?.clinic;
    if (!c?.id || byId.has(c.id)) continue;
    if (!ENTERABLE.has(link.status)) continue;
    byId.set(c.id, { id: c.id, name: c.name || '', city: c.city || '', currency: c.currency || '', owned: false });
  }
  return [...byId.values()];
}

/**
 * Memberships that exist but do not yet let the doctor in — a join request the
 * clinic has not answered.
 *
 * `toTargets` drops these on purpose (they are not entry tickets), which used
 * to make a doctor who had just sent a request indistinguishable from one who
 * had never heard of Parsina: both saw «you belong to no clinic». They are two
 * different situations and only one of them has anything left to do.
 *
 * An enterable clinic wins over a pending row for the SAME clinic — a request
 * that has since been approved is not still pending.
 */
function toPending(data, targets) {
  const enterable = new Set(targets.map((c) => c.id));
  const out = [];
  for (const link of data?.myClinics || []) {
    const c = link?.clinic;
    if (!c?.id || enterable.has(c.id) || ENTERABLE.has(link.status)) continue;
    out.push({ id: c.id, name: c.name || '', city: c.city || '', status: link.status || '' });
  }
  return out;
}

// Where the switch goes. Same tab: this is the same person continuing the same
// session in the other panel, not a second workspace to keep alongside — and a
// popup-blocked new tab would look like a dead button.
//
// THE TARGET IS NAMED IN THE PATH. This used to navigate to the bare panel root
// and let the clinic BFF infer a clinic on its own, which meant the switcher could
// offer a choice it could not actually make: a doctor at three clinics picked one
// and landed on whichever the inference ladder returned. The clinic panel routes
// on `/c/:ref/…` now, so the choice survives the crossing.
//
// With no target it still goes to the root, which is the honest thing to do when
// nothing was chosen — the panel resolves the person's most recent clinic there.
export function clinicPanelUrl(clinic) {
  const ref = typeof clinic === 'string' ? clinic : (clinic?.slug || clinic?.id || '');
  return ref ? `${CLINIC_APP_URL}/c/${encodeURIComponent(ref)}/` : `${CLINIC_APP_URL}/`;
}

// The one place the app leaves for the clinic panel. A function rather than an
// inline `window.location.assign` at each call site so both surfaces (sidebar,
// settings) leave the same way — and so leaving is observable in tests, where
// jsdom's `location` cannot be replaced.
export function enterClinicPanel(clinic) {
  window.location.assign(clinicPanelUrl(clinic));
}

// «ثبت نوبت» from an empty day. The doctor panel books nothing itself — نوبت‌ها
// are the clinic desk's — so a clinic OWNER with no appointments is sent to the
// clinic panel's پذیرش with the booking modal already open (`?book=`, consumed by
// clinic Reception.web.jsx on mount). The doctor's own id rides along so the
// modal skips its doctor-picker step; the panel falls back to the picker if the
// clinic cannot book for that id.
export function clinicBookingUrl(clinic, doctorId) {
  const who = doctorId ? encodeURIComponent(doctorId) : '1';
  return `${clinicPanelUrl(clinic)}reception?book=${who}`;
}

// A NEW tab, unlike `enterClinicPanel`: the doctor is not leaving for the other
// panel, they are stepping out to book one نوبت and coming straight back to the
// day they were looking at — which must still be there when they do.
export function openClinicBooking(clinic, doctorId) {
  window.open(clinicBookingUrl(clinic, doctorId), '_blank', 'noopener,noreferrer');
}

export function useClinicSwitch() {
  const [targets, setTargets] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTargets = useCallback(async () => {
    try {
      const data = await bffQuery(CLINIC_SWITCH);
      const next = toTargets(data);
      setTargets(next);
      setPending(toPending(data, next));
      setError(null);
    } catch (e) {
      // Chrome, not content: a clinicmanagement blip must never put an error in
      // the sidebar or block the doctor's own work. No clinic we can prove →
      // no button. But the failure is KEPT (`error`): «you have no clinic» is a
      // claim a screen may only make after a successful empty answer.
      setTargets([]);
      setPending([]);
      setError(e);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchTargets().finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [fetchTargets]);

  // `canSwitch` is false while loading too — the button appears once, when we
  // know, rather than flickering in and out of the sidebar on every mount.
  // The empty-day state needs the ownership split, not just «any clinic»: an
  // owner gets «ثبت نوبت», a member gets told whom to ask. First of each wins —
  // a doctor who owns two clinics still books at one desk at a time.
  const ownedClinic = targets.find((c) => c.owned) || null;
  const joinedClinic = targets.find((c) => !c.owned) || null;

  return { targets, pending, loading, error, canSwitch: !loading && targets.length > 0, ownedClinic, joinedClinic, refresh: fetchTargets };
}
